import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Loader2, Plus, Pencil, Trash2, Building2, Globe, Palette, Users,
  CreditCard, Settings, BarChart3, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const PLANS = [
  { value: "starter", label: "Starter", color: "#94A3B8", limit: "10 clients" },
  { value: "pro", label: "Pro", color: "#0099CC", limit: "50 clients" },
  { value: "enterprise", label: "Enterprise", color: "#10B981", limit: "Illimite" },
];

function AgencyForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    name: "", slug: "", domain: "", logo_url: "",
    primary_color: "#0099CC", secondary_color: "#10B981",
    contact_email: "", phone: "", address: "",
    subscription_plan: "starter", max_clients: 10,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Nom requis"); return; }
    setSaving(true);
    try { await onSave(form); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label className="text-[#94A3B8] text-xs">Nom de l'agence</Label>
          <Input value={form.name} onChange={e => set("name", e.target.value)} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Slug (URL)</Label>
          <Input value={form.slug} onChange={e => set("slug", e.target.value)} placeholder="mon-agence" className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Domaine personnalise</Label>
          <Input value={form.domain} onChange={e => set("domain", e.target.value)} placeholder="app.mon-agence.com" className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Logo URL</Label>
          <Input value={form.logo_url} onChange={e => set("logo_url", e.target.value)} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Couleur principale</Label>
          <div className="flex gap-2 mt-1">
            <input type="color" value={form.primary_color} onChange={e => set("primary_color", e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
            <Input value={form.primary_color} onChange={e => set("primary_color", e.target.value)} className="bg-[#0B0F19] border-[#2A3441] text-white" />
          </div>
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Couleur secondaire</Label>
          <div className="flex gap-2 mt-1">
            <input type="color" value={form.secondary_color} onChange={e => set("secondary_color", e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
            <Input value={form.secondary_color} onChange={e => set("secondary_color", e.target.value)} className="bg-[#0B0F19] border-[#2A3441] text-white" />
          </div>
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Email contact</Label>
          <Input value={form.contact_email} onChange={e => set("contact_email", e.target.value)} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Telephone</Label>
          <Input value={form.phone} onChange={e => set("phone", e.target.value)} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Plan</Label>
          <select value={form.subscription_plan} onChange={e => set("subscription_plan", e.target.value)}
            className="w-full mt-1 bg-[#0B0F19] border border-[#2A3441] text-white rounded-lg px-3 py-2 text-sm">
            {PLANS.map(p => <option key={p.value} value={p.value}>{p.label} ({p.limit})</option>)}
          </select>
        </div>
        <div><Label className="text-[#94A3B8] text-xs">Max clients</Label>
          <Input type="number" value={form.max_clients} onChange={e => set("max_clients", Number(e.target.value))} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
      </div>
      <div><Label className="text-[#94A3B8] text-xs">Adresse</Label>
        <Input value={form.address} onChange={e => set("address", e.target.value)} className="mt-1 bg-[#0B0F19] border-[#2A3441] text-white" />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving} className="bg-[#0099CC] hover:bg-[#0088B5] text-white">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Enregistrer
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
      </div>
    </form>
  );
}

export default function AgenciesPage() {
  const { isAdmin } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agencies`);
      setAgencies(res.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const create = async (data) => { await axios.post(`${API}/agencies`, data); toast.success("Agence creee"); setCreating(false); fetchData(); };
  const update = async (data) => { await axios.put(`${API}/agencies/${editing.id}`, data); toast.success("Mise a jour"); setEditing(null); fetchData(); };
  const remove = async (id) => { if (!window.confirm("Supprimer cette agence ?")) return; await axios.delete(`${API}/agencies/${id}`); toast.success("Supprimee"); fetchData(); };

  if (!isAdmin) {
    return (
      <div className="text-center py-16 bg-[#151B2B] rounded-xl border border-[#2A3441]">
        <Building2 className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Acces restreint</h3>
        <p className="text-[#94A3B8]">Cette section est reservee aux administrateurs.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agencies-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestion Multi-Agences</h1>
          <p className="text-[#94A3B8] mt-1">GLOTECH OS — Plateforme SaaS pour agences digitales</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="add-agency-btn">
          <Plus className="h-4 w-4 mr-2" />Nouvelle agence
        </Button>
      </div>

      {/* Create/Edit */}
      {creating && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Nouvelle agence</h3>
          <AgencyForm onSave={create} onCancel={() => setCreating(false)} />
        </div>
      )}
      {editing && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Modifier l'agence</h3>
          <AgencyForm initial={editing} onSave={update} onCancel={() => setEditing(null)} />
        </div>
      )}

      {/* Agencies List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" /></div>
      ) : agencies.length === 0 ? (
        <div className="text-center py-16 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <Building2 className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucune agence</h3>
          <p className="text-[#94A3B8]">Ajoutez votre premiere agence partenaire.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {agencies.map(a => {
            const plan = PLANS.find(p => p.value === a.subscription_plan) || PLANS[0];
            return (
              <div key={a.id} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 hover:border-[#0099CC]/30 transition-colors" data-testid={`agency-card-${a.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {a.logo_url ? (
                      <img src={a.logo_url} alt={a.name} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: `${a.primary_color}20`, color: a.primary_color }}>
                        {a.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="text-white font-semibold">{a.name}</h3>
                      {a.domain && <p className="text-[#0099CC] text-sm">{a.domain}</p>}
                    </div>
                  </div>
                  <Badge className="border-0 text-xs" style={{ backgroundColor: `${plan.color}15`, color: plan.color }}>{plan.label}</Badge>
                </div>

                {/* Branding preview */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: a.primary_color }} title="Couleur principale" />
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: a.secondary_color }} title="Couleur secondaire" />
                  <span className="text-xs text-[#64748b] ml-2">Branding</span>
                </div>

                {/* Info */}
                <div className="space-y-2 text-sm mb-4">
                  {a.contact_email && <div className="flex items-center gap-2 text-[#94A3B8]"><Globe className="h-3 w-3" />{a.contact_email}</div>}
                  <div className="flex items-center gap-2 text-[#94A3B8]"><Users className="h-3 w-3" />{a.clients_count || 0} / {a.max_clients} clients</div>
                  <div className="flex items-center gap-2 text-[#94A3B8]"><CreditCard className="h-3 w-3" />{a.subscription_status === "active" ? "Actif" : "Inactif"}</div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t border-[#2A3441]">
                  <Button size="sm" variant="outline" onClick={() => { setCreating(false); setEditing(a); }} className="border-[#2A3441] text-[#94A3B8] hover:text-white">
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />Modifier
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(a.id)} className="border-[#2A3441] text-[#EF4444] hover:bg-[#EF4444]/10">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />Supprimer
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
